"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { AuthContext } from "@/components/auth/Auth";
import { TabToggle } from "@/components/common/TabToggle";
import SandboxedExternalIframe from "@/components/common/SandboxedExternalIframe";
import FilePreview from "./file-upload/components/FilePreview";
import UploadArea from "./file-upload/components/UploadArea";
import BrowserWarning from "./file-upload/components/BrowserWarning";
import useFileUploader from "./file-upload/hooks/useFileUploader";
import useDragAndDrop from "./file-upload/hooks/useDragAndDrop";
import useAccessibility from "./file-upload/hooks/useAccessibility";
import type { MemesArtSubmissionFileProps } from "./file-upload/reducers/types";
import { isBrowserSupported } from "./file-upload/utils/browserDetection";
import { SUBMISSION_FILE_INPUT_ACCEPT } from "@/constants/submission-media.constants";
import { ALLOWED_INTERACTIVE_MEDIA_MIME_TYPES, DEFAULT_INTERACTIVE_MEDIA_MIME_TYPE, INTERACTIVE_MEDIA_PROVIDERS } from "./submission/constants/media";
const renderPreviewMessage = (primary: string, secondary: string) => <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-px-4 tw-text-center tw-text-sm tw-text-iron-400">
    <span>{primary}</span>
    <span className="tw-text-iron-500">{secondary}</span>
  </div>;

/**
 * Memes Art Submission File Component
 *
 * Handles uploading, previewing, and validating artwork files for meme submissions.
 * Supports images (PNG, JPG) and videos with browser compatibility detection.
 *
 * This version uses Blob URLs (createObjectURL) to preview any newly selected file,
 * which satisfies a CSP requiring 'blob:' rather than 'data:' for media.
 */
const MemesArtSubmissionFile: React.FC<MemesArtSubmissionFileProps> = t0 => {
  const $ = _c(80);
  const {
    artworkUploaded,
    artworkUrl,
    setArtworkUploaded,
    handleFileSelect,
    mediaSource,
    setMediaSource,
    externalHash,
    externalProvider,
    externalConstructedUrl,
    externalPreviewUrl,
    externalMimeType,
    externalError,
    externalValidationStatus,
    isExternalMediaValid,
    onExternalHashChange,
    onExternalProviderChange,
    onExternalMimeTypeChange,
    onClearExternalMedia
  } = t0;
  const {
    setToast
  } = useContext(AuthContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      supported: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [browserSupport, setBrowserSupport] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)").matches : false;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const prefersReducedMotion = t2;
  let t3;
  if ($[2] !== handleFileSelect || $[3] !== setArtworkUploaded || $[4] !== setToast) {
    t3 = {
      onFileSelect: handleFileSelect,
      setUploaded: setArtworkUploaded,
      showToast: setToast
    };
    $[2] = handleFileSelect;
    $[3] = setArtworkUploaded;
    $[4] = setToast;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    state,
    processFile,
    handleRetry,
    handleRemoveFile,
    dispatch
  } = useFileUploader(t3);
  let t4;
  if ($[6] !== dispatch) {
    t4 = newState => {
      dispatch({
        type: "SET_VISUAL_STATE",
        payload: newState
      });
    };
    $[6] = dispatch;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setVisualState = t4;
  const fileInputRef = useRef(null);
  let t5;
  if ($[8] !== mediaSource) {
    t5 = () => {
      if (mediaSource !== "upload") {
        return;
      }
      fileInputRef.current?.click();
    };
    $[8] = mediaSource;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleAreaClick = t5;
  const t6 = mediaSource === "upload" && !artworkUploaded;
  let t7;
  if ($[10] !== processFile || $[11] !== setVisualState || $[12] !== t6) {
    t7 = {
      enabled: t6,
      onFileDrop: processFile,
      setVisualState
    };
    $[10] = processFile;
    $[11] = setVisualState;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    dropAreaRef,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop
  } = useDragAndDrop(t7);
  const t8 = mediaSource === "upload" && !artworkUploaded;
  let t9;
  if ($[14] !== handleAreaClick || $[15] !== t8) {
    t9 = {
      isActive: t8,
      onAreaClick: handleAreaClick,
      prefersReducedMotion
    };
    $[14] = handleAreaClick;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const {
    handleKeyDown
  } = useAccessibility(t9);
  let t10;
  if ($[17] !== mediaSource || $[18] !== processFile) {
    t10 = e => {
      const file = e.target.files?.[0];
      if (file) {
        if (mediaSource === "upload") {
          processFile(file);
        }
        e.target.value = "";
      }
    };
    $[17] = mediaSource;
    $[18] = processFile;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const handleFileInputChange = t10;
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [{
      key: "upload",
      label: "Upload File",
      panelId: "memes-art-submission-upload-panel"
    }, {
      key: "url",
      label: "Interactive HTML",
      panelId: "memes-art-submission-interactive-panel"
    }];
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const tabOptions = t11 as const;
  let t12;
  if ($[21] !== onExternalMimeTypeChange || $[22] !== setMediaSource) {
    t12 = key => {
      if (key === "upload") {
        setMediaSource("upload");
        return;
      }
      if (key === "url") {
        onExternalMimeTypeChange(DEFAULT_INTERACTIVE_MEDIA_MIME_TYPE);
        setMediaSource("url");
      }
    };
    $[21] = onExternalMimeTypeChange;
    $[22] = setMediaSource;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const handleTabSelect = t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = INTERACTIVE_MEDIA_PROVIDERS.map(_temp);
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const providerOptions = t13;
  let t14;
  if ($[25] !== externalProvider || $[26] !== onExternalProviderChange) {
    t14 = key_0 => {
      if (key_0 === externalProvider) {
        return;
      }
      if (key_0 === "ipfs" || key_0 === "arweave") {
        onExternalProviderChange(key_0);
      }
    };
    $[25] = externalProvider;
    $[26] = onExternalProviderChange;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const handleProviderSelect = t14;
  let t15;
  let t16;
  if ($[28] !== setToast) {
    t15 = () => {
      const support = isBrowserSupported();
      setBrowserSupport(support);
      if (!support.supported && support.reason) {
        setToast({
          type: "warning",
          message: support.reason
        });
      }
    };
    t16 = [setToast];
    $[28] = setToast;
    $[29] = t15;
    $[30] = t16;
  } else {
    t15 = $[29];
    t16 = $[30];
  }
  useEffect(t15, t16);
  const {
    visualState,
    error,
    currentFile,
    objectUrl,
    videoCompatibility,
    isCheckingCompatibility
  } = state;
  const [previewUrl, setPreviewUrl] = useState(artworkUrl);
  let t17;
  let t18;
  if ($[31] !== artworkUrl || $[32] !== currentFile || $[33] !== objectUrl) {
    t17 = () => {
      if (currentFile) {
        const blobUrl = URL.createObjectURL(currentFile);
        setPreviewUrl(blobUrl);
        return () => {
          URL.revokeObjectURL(blobUrl);
        };
      }
      setPreviewUrl(objectUrl ?? artworkUrl);
    };
    t18 = [currentFile, objectUrl, artworkUrl];
    $[31] = artworkUrl;
    $[32] = currentFile;
    $[33] = objectUrl;
    $[34] = t17;
    $[35] = t18;
  } else {
    t17 = $[34];
    t18 = $[35];
  }
  useEffect(t17, t18);
  const showUploadUi = mediaSource === "upload";
  let t19;
  bb0: {
    if (externalValidationStatus === "pending") {
      let t20;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = renderPreviewMessage("Validating preview...", "We're verifying the gateway serves an HTML document.");
        $[36] = t20;
      } else {
        t20 = $[36];
      }
      t19 = t20;
      break bb0;
    }
    if (externalValidationStatus === "invalid" && externalError) {
      let t20;
      if ($[37] !== externalError) {
        t20 = renderPreviewMessage(externalError, "Only ipfs.io or arweave.net HTML documents can be embedded.");
        $[37] = externalError;
        $[38] = t20;
      } else {
        t20 = $[38];
      }
      t19 = t20;
      break bb0;
    }
    let t20;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = renderPreviewMessage("Provide a valid hash or CID to enable the preview.", "The final artwork is rendered securely inside a sandboxed iframe.");
      $[39] = t20;
    } else {
      t20 = $[39];
    }
    t19 = t20;
  }
  const previewFallback = t19;
  let t20;
  if ($[40] !== externalMimeType) {
    t20 = ALLOWED_INTERACTIVE_MEDIA_MIME_TYPES.find(type => type.value === externalMimeType);
    $[40] = externalMimeType;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const match = t20;
  const mediaTypeLabel = match?.label ?? externalMimeType;
  let t21;
  if ($[42] !== handleTabSelect || $[43] !== mediaSource) {
    t21 = <div className="tw-w-full tw-max-w-md tw-rounded-lg tw-border tw-border-iron-800 tw-bg-iron-950 tw-p-1"><TabToggle options={tabOptions} activeKey={mediaSource} onSelect={handleTabSelect} fullWidth={true} /></div>;
    $[42] = handleTabSelect;
    $[43] = mediaSource;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== artworkUploaded || $[46] !== browserSupport || $[47] !== currentFile || $[48] !== dropAreaRef || $[49] !== error || $[50] !== externalConstructedUrl || $[51] !== externalError || $[52] !== externalHash || $[53] !== externalPreviewUrl || $[54] !== externalProvider || $[55] !== handleAreaClick || $[56] !== handleDragEnter || $[57] !== handleDragLeave || $[58] !== handleDragOver || $[59] !== handleDrop || $[60] !== handleFileInputChange || $[61] !== handleKeyDown || $[62] !== handleProviderSelect || $[63] !== handleRemoveFile || $[64] !== handleRetry || $[65] !== isCheckingCompatibility || $[66] !== isExternalMediaValid || $[67] !== mediaTypeLabel || $[68] !== onClearExternalMedia || $[69] !== onExternalHashChange || $[70] !== previewFallback || $[71] !== previewUrl || $[72] !== showUploadUi || $[73] !== state.hasRecoveryOption || $[74] !== videoCompatibility || $[75] !== visualState) {
    t22 = showUploadUi ? <motion.div id="memes-art-submission-upload-panel" ref={dropAreaRef} {...!artworkUploaded && !prefersReducedMotion ? {
      whileHover: {
        scale: 1.002
      }
    } : {}} className={`tw-group tw-relative tw-h-full tw-w-full tw-overflow-hidden tw-rounded-xl tw-bg-gradient-to-br tw-from-iron-900 tw-to-iron-950 ${visualState === "dragging" ? "tw-border-2 tw-border-primary-500/60" : ""} ${visualState === "invalid" ? "tw-border-2 tw-border-red/60" : ""} ${visualState === "idle" && !artworkUploaded ? "hover:tw-border hover:tw-border-iron-700/80" : ""} tw-transition-all tw-duration-300 ${artworkUploaded ? "" : "tw-cursor-pointer"} `} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={!artworkUploaded ? handleAreaClick : undefined} role={artworkUploaded ? undefined : "button"} tabIndex={artworkUploaded ? -1 : 0} aria-label="Upload artwork" onKeyDown={handleKeyDown} aria-describedby={error ? "file-upload-error" : undefined} data-testid="artwork-upload-area"><input ref={fileInputRef} type="file" accept={SUBMISSION_FILE_INPUT_ACCEPT} className="tw-hidden" onChange={handleFileInputChange} data-testid="artwork-file-input" />{!browserSupport.supported && browserSupport.reason && <BrowserWarning reason={browserSupport.reason} />}{!artworkUploaded ? <UploadArea visualState={visualState} error={error} hasRecoveryOption={state.hasRecoveryOption} onRetry={handleRetry} /> : <FilePreview url={previewUrl} file={currentFile} onRemove={handleRemoveFile} videoCompatibility={videoCompatibility} isCheckingCompatibility={isCheckingCompatibility} />}</motion.div> : <div id="memes-art-submission-interactive-panel" className="tw-flex tw-flex-1 tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-iron-800 tw-bg-gradient-to-br tw-from-iron-900 tw-to-iron-950 tw-p-4"><div className="tw-flex tw-flex-col tw-gap-2"><span className="tw-text-sm tw-font-medium tw-text-iron-200">Hosting Network</span><div className="tw-inline-flex tw-rounded-lg tw-border tw-border-iron-800 tw-bg-iron-900 tw-p-1"><TabToggle options={providerOptions} activeKey={externalProvider} onSelect={handleProviderSelect} /></div></div><div className="tw-flex tw-flex-col tw-gap-2"><label htmlFor="memes-interactive-media-hash" className="tw-text-sm tw-font-medium tw-text-iron-200">Content Hash or Path</label><input id="memes-interactive-media-hash" type="text" autoComplete="off" className="tw-w-full tw-rounded-lg tw-border tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-2 tw-text-sm tw-text-iron-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-600" placeholder="bafy.../index.html" value={externalHash} onChange={event => onExternalHashChange(event.target.value)} aria-invalid={Boolean(externalError)} />{externalError && <p className="tw-text-red-400 tw-text-xs">{externalError}</p>}{externalConstructedUrl && !externalError && <p className="tw-text-xs tw-text-iron-400">Resulting URL{" "}<span className="tw-text-iron-100">{externalConstructedUrl}</span></p>}</div><div className="tw-flex tw-flex-col tw-gap-2"><span className="tw-text-sm tw-font-medium tw-text-iron-200">Media Type</span><div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-2 tw-text-sm tw-text-iron-200"><span>{mediaTypeLabel}</span><span className="tw-text-xs tw-text-iron-500">Fixed to interactive HTML (text/html)</span></div></div><div className="tw-flex tw-items-center"><button type="button" onClick={onClearExternalMedia} className="tw-rounded-md tw-border tw-border-iron-700 tw-bg-iron-900 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-iron-200 tw-transition hover:tw-bg-iron-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-40" disabled={!externalHash}>Clear Hash</button></div><div className="tw-flex tw-min-h-[360px] tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-lg tw-border tw-border-iron-800 tw-bg-iron-950">{isExternalMediaValid ? <SandboxedExternalIframe key={externalPreviewUrl} src={externalPreviewUrl} title="Interactive artwork preview" className="tw-bg-transparent" containerClassName="tw-flex-1 tw-flex tw-flex-col" fallback={renderPreviewMessage("Preview unavailable for unapproved domains or file types.", "Only ipfs.io or arweave.net HTML documents can be embedded.")} /> : <div className="tw-flex tw-flex-1 tw-flex-col">{previewFallback}</div>}</div></div>;
    $[45] = artworkUploaded;
    $[46] = browserSupport;
    $[47] = currentFile;
    $[48] = dropAreaRef;
    $[49] = error;
    $[50] = externalConstructedUrl;
    $[51] = externalError;
    $[52] = externalHash;
    $[53] = externalPreviewUrl;
    $[54] = externalProvider;
    $[55] = handleAreaClick;
    $[56] = handleDragEnter;
    $[57] = handleDragLeave;
    $[58] = handleDragOver;
    $[59] = handleDrop;
    $[60] = handleFileInputChange;
    $[61] = handleKeyDown;
    $[62] = handleProviderSelect;
    $[63] = handleRemoveFile;
    $[64] = handleRetry;
    $[65] = isCheckingCompatibility;
    $[66] = isExternalMediaValid;
    $[67] = mediaTypeLabel;
    $[68] = onClearExternalMedia;
    $[69] = onExternalHashChange;
    $[70] = previewFallback;
    $[71] = previewUrl;
    $[72] = showUploadUi;
    $[73] = state.hasRecoveryOption;
    $[74] = videoCompatibility;
    $[75] = visualState;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  let t23;
  if ($[77] !== t21 || $[78] !== t22) {
    t23 = <div className="tw-flex tw-h-full tw-flex-col tw-gap-4">{t21}{t22}</div>;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  return t23;
};
export default React.memo(MemesArtSubmissionFile);
function _temp(provider) {
  return {
    key: provider.key,
    label: provider.label,
    panelId: `memes-art-submission-provider-${provider.key}`
  };
}